'use client';

import { useEffect, useState, type FC } from 'react';

import type { TLotSaleState } from '@/shared/auctionTypes';

/**
 * The card's date bar: a live countdown to the sale, ticking in the browser
 * (spec 013).
 *
 * Listing pages are ISR-cached for an hour, which is why the lot page keeps its
 * "za 3 dni" coarse (spec 010, R8): anything rendered on the server is up to an
 * hour stale while looking precise. This component takes the other route — the
 * server renders the same coarse figure, and once mounted the browser computes
 * the remaining time from the sale TIMESTAMP at view time and ticks it. Nothing
 * precise is ever served from cache.
 *
 * Every state carries words, not just numbers: a live lane says so, a sale that
 * already ran keeps the struck-through date and the "may have changed" note, and
 * a lot without a date says "Brak daty" rather than counting to nothing.
 */

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

/**
 * The colour rule for time-to-sale, shared by the cards and the lot page:
 *
 *   ≥ 3 days      neutral   — nothing to do yet
 *   < 3 days      amber     — arrange the deposit and the checks now
 *   < 24 hours    red       — last chance to act
 *   running       green     — the live pill, handled by the caller
 *
 * Never the only signal: the figure itself says how long, and the live state
 * carries words.
 */
export const COUNTDOWN_AMBER_BELOW_MS = 3 * DAY;
export const COUNTDOWN_RED_BELOW_MS = DAY;

const urgencyClass = (ms: number): string =>
  ms < COUNTDOWN_RED_BELOW_MS ? 'text-red-400' : ms < COUNTDOWN_AMBER_BELOW_MS ? 'text-amber-300' : 'text-foreground';

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * "2 dni 14 godz. 05 min" beyond a day, "14 godz. 05 min 33 s" inside one,
 * "05 min 33 s" inside an hour. Units are spelled out: a bare "10:56:35" does
 * not say whether it is a time of day or a duration.
 */
export const formatCountdown = (ms: number): string => {
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  const minutes = Math.floor((ms % HOUR) / MINUTE);
  const seconds = Math.floor((ms % MINUTE) / 1000);

  if (days >= 1) return `${days} ${days === 1 ? 'dzień' : 'dni'} ${pad(hours)} godz. ${pad(minutes)} min`;
  if (hours >= 1) return `${hours} godz. ${pad(minutes)} min ${pad(seconds)} s`;

  return `${minutes} min ${pad(seconds)} s`;
};

/** What the server can say without lying: whole days only. */
const formatCoarse = (ms: number): string => {
  const days = Math.floor(ms / DAY);

  if (days >= 2) return `za ${days} dni`;
  if (days === 1) return 'za 1 dzień';

  return 'dziś';
};

type TAuctionSaleCountdown = {
  saleDate: Date | null;
  saleState: TLotSaleState | null;
  /** The sale's calendar day has elapsed (see `isPast`). */
  isStaleDate: boolean;
  /** Polish calendar date, already formatted. */
  formattedDate: string | null;
  /**
   * `card` (default) renders the whole date bar with its label. `detail` renders
   * only the countdown line, for the lot page's own date panel.
   */
  variant?: 'card' | 'detail';
};

export const AuctionSaleCountdown: FC<TAuctionSaleCountdown> = ({
  saleDate,
  saleState,
  isStaleDate,
  formattedDate,
  variant = 'card',
}) => {
  // `null` until mounted, so server and client render the same coarse text and
  // hydration never disagrees over the seconds.
  const [now, setNow] = useState<number | null>(null);
  const target = saleDate?.getTime() ?? null;
  // A finished sale never counts down, even when its scheduled lane is still
  // ahead: the provider marks lots sold before their lane (pre-bid accepted,
  // buy-now taken) as `finished` with a future `auction_at` — measured, 16 of
  // 160 upcoming Ford lots on 2026-09-04.
  const isFinished = saleState === 'finished';
  const isCounting = target !== null && !isStaleDate && !isFinished && saleState !== 'live';

  useEffect(() => {
    if (!isCounting) return;

    setNow(Date.now());

    // Seconds only matter inside the last day; a minute tick is plenty before.
    const interval = target - Date.now() < DAY ? 1000 : MINUTE;
    const timer = window.setInterval(() => setNow(Date.now()), interval);

    return () => window.clearInterval(timer);
  }, [isCounting, target]);

  const labelClass = 'font-roboto text-xs uppercase tracking-wider text-neutral-500';
  const dateClass = 'font-roboto text-xs text-neutral-500';

  if (isStaleDate) {
    if (variant === 'detail') return null;

    return (
      <div className="min-w-0">
        <p className={labelClass}>Termin minął</p>
        <p className="font-oswald text-sm text-neutral-500 line-through">{formattedDate}</p>
        <p className="mt-1 text-xs text-neutral-500">Termin mógł ulec zmianie — potwierdzimy przed licytacją.</p>
      </div>
    );
  }

  const livePill = (
    <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/15 px-3 py-1 font-oswald text-sm uppercase tracking-wider text-emerald-300">
      <span
        aria-hidden
        className="inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-400 motion-safe:animate-pulse"
      />
      Trwa teraz
    </span>
  );

  if (isFinished) {
    if (variant === 'detail') return null;

    return (
      <div className="min-w-0">
        <p className={labelClass}>Licytacja</p>
        <p className="font-oswald text-lg leading-tight text-neutral-400">Zakończona</p>
        {formattedDate && <p className={dateClass}>{formattedDate}</p>}
      </div>
    );
  }

  if (saleState === 'live') {
    if (variant === 'detail') return <p className="mt-2">{livePill}</p>;

    return (
      <div className="min-w-0">
        <p className={labelClass}>Licytacja</p>
        <p className="mt-0.5">{livePill}</p>
        {formattedDate && <p className={`mt-1 ${dateClass}`}>{formattedDate}</p>}
      </div>
    );
  }

  if (target === null) {
    if (variant === 'detail') return null;

    return (
      <div className="min-w-0">
        <p className={labelClass}>Data aukcji</p>
        <p className="font-oswald text-sm text-neutral-300">Brak daty</p>
      </div>
    );
  }

  const remaining = Math.max(0, target - (now ?? Date.now()));
  const figure =
    remaining <= 0 ? 'Dziś' : now === null ? formatCoarse(remaining) : formatCountdown(remaining);

  if (variant === 'detail') {
    return (
      <p className="mt-1 text-sm">
        <span className="text-neutral-500">Do aukcji: </span>
        <span className={`font-oswald text-base tabular-nums ${urgencyClass(remaining)}`} aria-live="off">
          {figure}
        </span>
      </p>
    );
  }

  return (
    <div className="min-w-0">
      <p className={labelClass}>Do aukcji</p>
      <p
        className={`font-oswald text-lg leading-tight tabular-nums ${urgencyClass(remaining)}`}
        // The exact figure is announced once, not on every tick.
        aria-live="off"
      >
        {figure}
      </p>
      {formattedDate && <p className={dateClass}>{formattedDate}</p>}
    </div>
  );
};
